import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom'
import { Toaster } from 'sonner'
import { AuthProvider } from './contexts/AuthContext'
import { ThemeProvider } from './contexts/ThemeContext'
import { LabelsProvider } from './contexts/LabelsContext'
import { I18nProvider } from './i18n/I18nContext'
import { LocalizationProvider } from './contexts/LocalizationContext'
import { UpgradeModalProvider } from './contexts/UpgradeModalContext'
import { ProtectedRoute } from './components/ProtectedRoute'
import { MainLayout } from './components/MainLayout'
import { LoginPage } from './pages/LoginPage'
import { SignupPage } from './pages/SignupPage'
import ForgotPasswordPage from './pages/ForgotPasswordPage'
import ResetPasswordPage from './pages/ResetPasswordPage'
import VerifyEmailPage from './pages/VerifyEmailPage'
import AcceptInvitationPage from './pages/AcceptInvitationPage'
import BillingPage from './pages/BillingPage'
import Dashboard from './pages/Dashboard'
import ProjectList from './pages/ProjectList'
import ProjectDashboard from './pages/ProjectDashboard'
import CreateProject from './pages/CreateProject'
import ProjectWorkspace from './pages/ProjectWorkspace'
import RedirectToFirstProjectWorkspace from './pages/RedirectToFirstProjectWorkspace'
import PortfolioList from './pages/PortfolioList'
import PortfolioDashboard from './pages/PortfolioDashboard'
import PortfolioWorkspace from './pages/PortfolioWorkspace'
import ProgramList from './pages/ProgramList'
import ProgramDashboard from './pages/ProgramDashboard'
import ProgramWorkspace from './pages/ProgramWorkspace'
import CreateProgram from './pages/CreateProgram'
import ProgramAnalytics from './pages/ProgramAnalytics'
import TaskList from './pages/TaskList'
import CreateTask from './pages/CreateTask'
import TaskDetails from './pages/TaskDetails'
import ReportsBuilderPage from './pages/ReportsBuilderPage'
import CreatePortfolio from './pages/CreatePortfolio'
import EditPortfolio from './pages/EditPortfolio'
import EditProject from './pages/EditProject'
import EditProgram from './pages/EditProgram'
// ExecutiveCommandCenter available at /dashboard via Dashboard route
import AdminSettings from './pages/AdminSettings'
import RisksPage from './pages/RisksPage'
import PortfolioAnalytics from './pages/PortfolioAnalytics'
import PortfolioGovernance from './pages/PortfolioGovernance'
import GovernanceSurfaces from './pages/GovernanceSurfaces'
import AIAssistant from './pages/AIAssistant'
import LessonsLearned from './pages/LessonsLearned'
import MyDay from './pages/MyDay'
import OnboardingWizard from './pages/OnboardingWizard'
import ShareLinkView from './pages/ShareLinkView'
import ApprovePage from './pages/ApprovePage'
import SpreadsheetImport from './pages/SpreadsheetImport'
import IntakePage from './pages/IntakePage'
import SsoCallbackPage from './pages/SsoCallbackPage'
import IntegrationsSettings from './pages/IntegrationsSettings'
import IntegrationBreadth from './pages/IntegrationBreadth'
import { PlanGate } from './components/common/PlanGate'
import ClientPortalPage from './pages/ClientPortalPage'
import BIConnectorPage from './pages/BIConnectorPage'
import DepartmentsPage from './pages/DepartmentsPage'
import TeamsPage from './pages/TeamsPage'
import RecycleBin from './pages/RecycleBin'
import StatusSetsAdminPage from './pages/StatusSetsAdminPage'
import PMOLanding from './pages/PMOLanding'
import ProductsPage from './pages/ProductsPage'
import ProductDetailPage from './pages/ProductDetailPage'
import StrategyPage from './pages/StrategyPage'
import CapacityPage from './pages/CapacityPage'
import { AnalyticsTracker } from './components/common/AnalyticsTracker'
import GuestView from './pages/GuestView'
import NotificationPreferences from './pages/NotificationPreferences'
import TemplateAdmin from './pages/TemplateAdmin'
import './App.css'

function App() {
  return (
    <ThemeProvider>
      <I18nProvider>
      <AuthProvider>
        <LocalizationProvider>
        <LabelsProvider>
        <Router>
        <AnalyticsTracker />
        <UpgradeModalProvider>
        <Routes>
          {/* Public Routes */}
          <Route path="/login" element={<LoginPage />} />
          <Route path="/signup" element={<SignupPage />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          <Route path="/verify-email" element={<VerifyEmailPage />} />
          <Route path="/sso/callback" element={<SsoCallbackPage />} />
          <Route path="/accept-invitation" element={<AcceptInvitationPage />} />
          <Route
            path="/onboarding"
            element={
              <ProtectedRoute>
                <OnboardingWizard />
              </ProtectedRoute>
            }
          />
          <Route path="/share/:token" element={<ShareLinkView />} />
          <Route path="/approve" element={<ApprovePage />} />
          <Route path="/guest/:token" element={<GuestView />} />

          {/* PMO landing — full-screen, outside MainLayout */}
          <Route
            path="/"
            element={
              <ProtectedRoute>
                <PMOLanding />
              </ProtectedRoute>
            }
          />

          {/* Protected Routes */}
          <Route
            path="/"
            element={
              <ProtectedRoute>
                <MainLayout />
              </ProtectedRoute>
            }
          >
            <Route path="home" element={<MyDay />} />
            <Route path="dashboard" element={<Dashboard />} />
            {/* Placeholder routes - will be implemented in Phase 4 */}
            <Route path="project" element={<ProjectList />} />
            <Route path="project/create" element={<CreateProject />} />
            <Route path="project/dashboard" element={<ProjectDashboard />} />
            <Route path="project/workspace" element={<RedirectToFirstProjectWorkspace />} />
            <Route path="project/:id/edit" element={<EditProject />} />
            <Route path="project/:id/:tab" element={<ProjectWorkspace />} />
            <Route path="project/:id" element={<ProjectWorkspace />} />
            <Route path="task" element={<TaskList />} />
            <Route path="task/create" element={<CreateTask />} />
            <Route path="task/:id" element={<TaskDetails />} />
            <Route path="reports" element={<ReportsBuilderPage />} />
            <Route path="portfolio" element={<PortfolioList />} />
            <Route path="portfolio/create" element={<CreatePortfolio />} />
            <Route path="portfolio/:id/edit" element={<EditPortfolio />} />
            <Route path="portfolio/dashboard" element={<PortfolioDashboard />} />
            <Route path="portfolio/:id" element={<PortfolioWorkspace />} />
            <Route path="portfolio/analytics" element={<PortfolioAnalytics />} />
            <Route path="governance" element={<PortfolioGovernance />} />
            <Route path="governance/surfaces" element={<GovernanceSurfaces />} />
            <Route path="portfolio/governance" element={<Navigate to="/governance" replace />} />
            <Route path="program" element={<ProgramList />} />
            <Route path="program/create" element={<CreateProgram />} />
            <Route path="program/dashboard" element={<ProgramDashboard />} />
            <Route path="program/:id/edit" element={<EditProgram />} />
            <Route path="program/:id" element={<ProgramWorkspace />} />
            <Route path="program/analytics" element={<ProgramAnalytics />} />
            <Route path="risks" element={<RisksPage />} />
            <Route path="lessons" element={<LessonsLearned />} />
            <Route
              path="ai"
              element={
                <PlanGate minimum="growth" featureName="AI Assistant + Insights">
                  <AIAssistant />
                </PlanGate>
              }
            />
            <Route path="settings" element={<AdminSettings />} />
            <Route path="import" element={<SpreadsheetImport />} />
            <Route path="intake" element={<IntakePage />} />
            <Route path="strategy" element={<StrategyPage />} />
            <Route
              path="product"
              element={
                <PlanGate minimum="growth" featureName="Products">
                  <ProductsPage />
                </PlanGate>
              }
            />
            <Route
              path="product/:id"
              element={
                <PlanGate minimum="growth" featureName="Products">
                  <ProductDetailPage />
                </PlanGate>
              }
            />
            <Route
              path="capacity"
              element={
                <PlanGate minimum="growth" featureName="Capacity">
                  <CapacityPage />
                </PlanGate>
              }
            />
            <Route
              path="integrations"
              element={
                <PlanGate minimum="growth" featureName="Integrations">
                  <IntegrationsSettings />
                </PlanGate>
              }
            />
            <Route
              path="integrations/breadth"
              element={
                <PlanGate minimum="growth" featureName="Extended integrations">
                  <IntegrationBreadth />
                </PlanGate>
              }
            />
            {/* Part 11 — Templates, Admin Tooling, Power-User Ergonomics */}
            <Route path="templates" element={<TemplateAdmin />} />
            {/* Phase 4 — Billing */}
            <Route path="billing" element={<BillingPage />} />
            {/* Phase 3 — Scale */}
            <Route path="client-portal" element={<ClientPortalPage />} />
            <Route path="bi-connector" element={<BIConnectorPage />} />
            <Route path="departments" element={<DepartmentsPage />} />
            <Route path="teams" element={<TeamsPage />} />
            <Route path="recycle-bin" element={<RecycleBin />} />
            <Route path="settings/status-sets" element={<StatusSetsAdminPage />} />
            <Route path="settings/notifications" element={<NotificationPreferences />} />
            {/* Plural aliases people type by hand (and old bookmarks) */}
            <Route path="projects" element={<Navigate to="/project" replace />} />
            <Route path="programs" element={<Navigate to="/program" replace />} />
            <Route path="portfolios" element={<Navigate to="/portfolio" replace />} />
            <Route path="products" element={<Navigate to="/product" replace />} />
            <Route path="tasks" element={<Navigate to="/task" replace />} />
          </Route>

          {/* Catch all - redirect to home */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
        </UpgradeModalProvider>
        </Router>
        <Toaster position="top-right" richColors />
        </LabelsProvider>
        </LocalizationProvider>
      </AuthProvider>
      </I18nProvider>
    </ThemeProvider>
  )
}

export default App

